uniform vec3 uColor;
uniform vec3 uHoverColor;
uniform vec3 uAirplaneColorFar;
uniform vec3 uAirplaneColorClose;
uniform vec3 uDustWakeColor;
uniform float uRadarFade;
uniform float uRadarShowRing;
uniform float uRadarPermanent;
uniform vec3 uRainColor;
uniform vec3 uRadarRingColorLow;
uniform vec3 uRadarRingColorMid;
uniform vec3 uRadarRingColorHigh;
uniform float uRainParticleWidth;
uniform float uRainParticleLength;
varying float vMaskAlpha;
varying float vHoverMix;
varying float vAirplaneMix;
varying float vAirplaneProximity;
varying float vDustWakeMix;
varying float vRadarMix;
varying float vRadarHeight;
varying float vRadarRainOpacity;
varying float vRainColorBlend;
varying float vRainProgress;
varying vec2 vRainDirection;
#include <fog_pars_fragment>

void main() {
  vec2 pc = gl_PointCoord - 0.5;
  float r = length(pc);
  
  // Rotate point coordinates to align dash with world-space "down" direction
  // This makes rain always appear to fall downward regardless of camera angle
  vec2 rainDir = normalize(vRainDirection);
  // Rotation matrix to align Y-axis with rain direction
  // rainDir points "down" in screen space, so we rotate pc to match
  vec2 rotatedPc;
  rotatedPc.x = pc.x * (-rainDir.y) + pc.y * (-rainDir.x);
  rotatedPc.y = pc.x * (-rainDir.x) + pc.y * (rainDir.y);
  
  // Disc shape (normal point) - uses unrotated coordinates
  float disc = 1.0 - smoothstep(0.45, 0.5, r);
  
  // Vertical line shape (rain streak) - uses rotated coordinates
  // Thickness is fully independent of length by compensating for point size scaling
  // Both the width and the edge softness are scaled inversely with length
  float lengthScale = max(1.0, uRainParticleLength);
  float compensation = 7.0 / lengthScale; // 7.0 is reference length
  float compensatedWidth = uRainParticleWidth * compensation;
  float edgeSoftness = 0.02 * compensation;
  float xMask = 1.0 - smoothstep(compensatedWidth, compensatedWidth + edgeSoftness, abs(rotatedPc.x));
  float yMask = 1.0 - smoothstep(0.48, 0.5, abs(rotatedPc.y));
  float lineMask = xMask * yMask;
  
  // Shape transition: starts during impact phase (0.88) for visible impact effect
  // The impact size parameter controls how large the circle is at transition
  float shapeBlend = smoothstep(0.88, 0.98, vRainProgress);
  float shape = mix(lineMask, disc, shapeBlend);
  
  // Radar can reveal particles beyond the mask
  float radarAlpha = vRadarMix * uRadarFade;
  // If permanent mode is on, we force visibility (alpha=1.0)
  float effectiveAlpha = max(max(vMaskAlpha, radarAlpha), uRadarPermanent);
  
  // Rain opacity: start more visible (0.6) and ramp to full
  // This ensures lines are visible during the fall
  float rainAlpha = vRadarRainOpacity;
  float alpha = shape * effectiveAlpha * rainAlpha;
  if (alpha <= 0.001) discard;
  
  // Start with base color
  vec3 col = uColor;
  // Apply mouse hover color
  col = mix(col, uHoverColor, vHoverMix);
  // Apply airplane hover - blend between far (orange) and close (red) based on proximity
  vec3 airplaneColor = mix(uAirplaneColorFar, uAirplaneColorClose, vAirplaneProximity);
  col = mix(col, airplaneColor, vAirplaneMix);
  
  // Apply dust wake effect - orange glow for particles kicked up by fast, low-flying airplane
  // This takes priority over other colors for immediate visual feedback
  col = mix(col, uDustWakeColor, vDustWakeMix * 0.85);
  
  // Rain color - applied to falling particles, crossfades to ring/base color
  // vRainColorBlend: 0 = rain color, 1 = final color
  if (vRainColorBlend < 1.0) {
    // Calculate the target color (ring color if enabled, otherwise base)
    vec3 targetColor = col;
    if (uRadarShowRing > 0.5) {
      targetColor = vRadarHeight < 0.5 
        ? mix(uRadarRingColorLow, uRadarRingColorMid, vRadarHeight * 2.0)
        : mix(uRadarRingColorMid, uRadarRingColorHigh, (vRadarHeight - 0.5) * 2.0);
    }
    // Crossfade from rain color to target color
    col = mix(uRainColor, targetColor, vRainColorBlend);
  } else if (uRadarShowRing > 0.5) {
    // Fully transitioned, apply ring color if enabled
    vec3 radarColor = vRadarHeight < 0.5 
      ? mix(uRadarRingColorLow, uRadarRingColorMid, vRadarHeight * 2.0)
      : mix(uRadarRingColorMid, uRadarRingColorHigh, (vRadarHeight - 0.5) * 2.0);
    
    // In permanent mode, we use full radar color. Otherwise, we blend with radarAlpha.
    float mixFactor = max(radarAlpha * 0.9, uRadarPermanent * 0.9);
    col = mix(col, radarColor, mixFactor);
  }
  
  gl_FragColor = vec4(col, alpha);
  #include <fog_fragment>
}
